import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { AppDataProvider } from "./context/AppDataContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LandingPage from "./pages/LandingPage";
import ShopPage from "./pages/ShopPage";
import FeaturesPage from "./pages/FeaturesPage";
import SensorsPage from "./pages/SensorsPage";
import ReportsPage from "./pages/ReportsPage";
import ContactPage from "./pages/ContactPage";
import SupportPage from "./pages/SupportPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProductPage from "./pages/ProductPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import CartPage from "./pages/CartPage";
import ShippingPage from "./pages/ShippingPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentConfirmationPage from "./pages/PaymentConfirmationPage";
import DashboardPage from "./pages/DashboardPage";
import DeviceListPage from "./pages/DeviceListPage";
import DeviceDetailPage from "./pages/DeviceDetailPage";
import AddDevicePage from "./pages/AddDevicePage";
import GatewayListPage from "./pages/GatewayListPage";
import GatewayDetailPage from "./pages/GatewayDetailPage";
import AddGatewayPage from "./pages/AddGatewayPage";
import LilyGoCommissionPage from "./pages/LilyGoCommissionPage";
import GatewaySettingsPage from "./pages/GatewaySettingsPage";
import GatewayConfigPage from "./pages/GatewayConfigPage";
import SubscriptionPage from "./pages/dashboard/SubscriptionPage";
import MapsPage from "./pages/MapsPage";
import ZoneListPage from "./pages/ZoneListPage";
import ZoneDetailPage from "./pages/ZoneDetailPage";
import AddZonePage from "./pages/AddZonePage";
import SettingsPage from "./pages/SettingsPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import RulesPage from "./pages/RulesPage";
import OrdersPage from "./pages/OrdersPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ReturnPolicyPage from "./pages/ReturnPolicyPage";
import PricingPage from "./pages/PricingPage";
import BillingPage from "./pages/BillingPage";
import VerifyListPage from "./pages/VerifyListPage";
import VerifyDetailPage from "./pages/VerifyDetailPage";
import PublicReportPage from "./pages/PublicReportPage";
import IntelligencePage from "./pages/IntelligencePage";
import PipelinesListPage from "./pages/PipelinesListPage";
import EtlFlowPage from "./pages/EtlFlowPage";
import ShipmentsListPage from "./pages/ShipmentsListPage";
import ShipmentDetailPage from "./pages/ShipmentDetailPage";
import NotFoundPage from "./pages/NotFoundPage";
import CopilotWidget from "./components/CopilotWidget";
import "./App.css";

function AppRoutes() {
  return (
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/sensors" element={<SensorsPage />} />
      <Route path="/reports" element={<ReportsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/product/:id" element={<ProductPage />} />
      {/* Auth Pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/return-policy" element={<ReturnPolicyPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      {/* Public Verify Report */}
      <Route path="/verify/:publicId" element={<PublicReportPage />} />
      {/* Cart & Checkout */}
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout/shipping" element={<ShippingPage />} />
      <Route path="/checkout/payment" element={<CheckoutPage />} />
      <Route path="/checkout/confirmation" element={<PaymentConfirmationPage />} />
      <Route path="/order-confirmation" element={<PaymentConfirmationPage />} />
      {/* Dashboard - Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/dashboard/devices" element={<ProtectedRoute><DeviceListPage /></ProtectedRoute>} />
      <Route path="/dashboard/devices/:id" element={<ProtectedRoute><DeviceDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/devices/add" element={<ProtectedRoute><AddDevicePage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways" element={<ProtectedRoute><GatewayListPage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways/config" element={<ProtectedRoute><GatewayConfigPage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways/add" element={<ProtectedRoute><AddGatewayPage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways/commission" element={<ProtectedRoute><LilyGoCommissionPage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways/:id/settings" element={<ProtectedRoute><GatewaySettingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/gateways/:id" element={<ProtectedRoute><GatewayDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/zones" element={<ProtectedRoute><ZoneListPage /></ProtectedRoute>} />
      <Route path="/dashboard/maps" element={<ProtectedRoute><MapsPage /></ProtectedRoute>} />
      <Route path="/dashboard/zones/add" element={<ProtectedRoute><AddZonePage /></ProtectedRoute>} />
      <Route path="/dashboard/zones/:id/edit" element={<ProtectedRoute><AddZonePage /></ProtectedRoute>} />
      <Route path="/dashboard/zones/:id" element={<ProtectedRoute><ZoneDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/developers" element={<ProtectedRoute><ApiDocsPage /></ProtectedRoute>} />
      <Route path="/dashboard/rules" element={<ProtectedRoute><RulesPage /></ProtectedRoute>} />
      <Route path="/dashboard/verify" element={<ProtectedRoute><VerifyListPage /></ProtectedRoute>} />
      <Route path="/dashboard/verify/:id" element={<ProtectedRoute><VerifyDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/intelligence" element={<ProtectedRoute><IntelligencePage /></ProtectedRoute>} />
      <Route path="/dashboard/pipelines" element={<ProtectedRoute><PipelinesListPage /></ProtectedRoute>} />
      <Route path="/dashboard/pipelines/:id" element={<ProtectedRoute><EtlFlowPage /></ProtectedRoute>} />
      <Route path="/dashboard/shipments" element={<ProtectedRoute><ShipmentsListPage /></ProtectedRoute>} />
      <Route path="/dashboard/shipments/:id" element={<ProtectedRoute><ShipmentDetailPage /></ProtectedRoute>} />
      {/* 404 Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function AppLayout() {
  const location = useLocation();
  const isPipelines = /^\/dashboard\/pipelines\/[^/]+$/.test(location.pathname);
  const isMaps = location.pathname.startsWith('/dashboard/maps');

  return (
    <div className={`app-container${isPipelines ? ' app-container--pipelines' : ''}${isMaps ? ' app-container--maps' : ''}`}>
      <Header />
      <main className={`main-content${isPipelines ? ' main-content--pipelines' : ''}${isMaps ? ' main-content--maps' : ''}`}>
        <AppRoutes />
      </main>
      {!isPipelines && !isMaps && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppDataProvider>
          <CartProvider>
            <BrowserRouter>
              <AppLayout />
              <CopilotWidget />
            </BrowserRouter>
          </CartProvider>
        </AppDataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
